import React, { useState, useEffect } from 'react';
import { userAPI } from '../../api';

const RealTimeBonanzaProgressTracker = () => {
  const [trackerData, setTrackerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBonanzaProgress();
  }, []);

  const fetchBonanzaProgress = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await userAPI.getBonanzaProgress();
      setTrackerData(response.data?.data || null);
    } catch (err) {
      console.error('Error fetching bonanza progress:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load bonanza tracker');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return '✅';
      case 'PENDING':
        return '⏳';
      case 'LOCKED':
        return '🔒';
      default:
        return '•';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'from-green-500 to-green-600';
      case 'PENDING':
        return 'from-yellow-500 to-yellow-600';
      case 'LOCKED':
        return 'from-gray-400 to-gray-500';
      default:
        return 'from-gray-300 to-gray-400';
    }
  };

  const getTextColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-600';
      case 'PENDING':
        return 'text-yellow-600';
      case 'LOCKED':
        return 'text-gray-500';
      default:
        return 'text-gray-600';
    }
  };

  const getBgColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-50 border-green-200';
      case 'PENDING':
        return 'bg-yellow-50 border-yellow-200';
      case 'LOCKED':
        return 'bg-gray-50 border-gray-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E4572E] border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <div className="text-red-600 font-semibold mb-2">Error Loading Bonanza Progress</div>
        <div className="text-red-500 text-sm mb-4">{error}</div>
        <button
          onClick={fetchBonanzaProgress}
          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!trackerData) {
    return <div className="text-center py-12">No data available</div>;
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#0F2F3D] mb-2 flex items-center gap-3">
          <span className="text-4xl">🎯</span>
          Real-Time Bonanza Progress
        </h1>
        <p className="text-gray-600">Track your qualified stages</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Leader Count */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-4xl font-bold">{trackerData.leaderCount.toLocaleString()}</div>
              <div className="text-blue-100 text-sm mt-1">Active Leaders</div>
            </div>
            <div className="text-5xl opacity-30">👥</div>
          </div>
        </div>

        {/* Completed Levels */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-4xl font-bold">{trackerData.completedLevels}</div>
              <div className="text-green-100 text-sm mt-1">Stages Qualified</div>
            </div>
            <div className="text-5xl opacity-30">🏆</div>
          </div>
        </div>

        {/* Current Active Level */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-4xl font-bold">
                {trackerData.currentActiveLevel === 0 ? '—' : trackerData.currentActiveLevel}
              </div>
              <div className="text-purple-100 text-sm mt-1">Current Stage</div>
            </div>
            <div className="text-5xl opacity-30">⚡</div>
          </div>
        </div>
      </div>

      {/* Bonanza Levels */}
      <div className="space-y-4">
        {trackerData.levels.map((level, index) => (
          <div
            key={level.level}
            className={`border-2 rounded-xl p-6 transition-all ${getBgColor(level.status)} ${
              level.status === 'LOCKED' ? 'opacity-70' : 'opacity-100'
            }`}
          >
            {/* Level Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                {/* Status Icon */}
                <div className={`text-4xl ${getStatusIcon(level.status)}`}>
                  {getStatusIcon(level.status)}
                </div>

                {/* Level Info */}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-[#0F2F3D]">{level.name}</h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                      level.status === 'COMPLETED'
                        ? 'bg-green-200 text-green-700'
                        : level.status === 'PENDING'
                        ? 'bg-yellow-200 text-yellow-700'
                        : 'bg-gray-200 text-gray-700'
                    }`}>
                      {level.status === 'COMPLETED' && '✅ Qualified'}
                      {level.status === 'PENDING' && '⏳ Pending'}
                      {level.status === 'LOCKED' && '🔒 Locked'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Requires <span className="font-bold text-gray-900">{level.requiredLeaders.toLocaleString()}</span> active leaders
                  </p>
                </div>
              </div>
            </div>

            {/* Progress Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700">
                  Leaders: <span className="font-bold text-gray-900">{trackerData.leaderCount.toLocaleString()}</span>
                </span>
                <span className={`font-bold ${getTextColor(level.status)}`}>
                  {level.progress.toFixed(1)}%
                </span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className={`bg-gradient-to-r ${getStatusColor(level.status)} h-full rounded-full transition-all duration-500`}
                  style={{ width: `${level.progress}%` }}
                ></div>
              </div>

              {/* Status Message */}
              <div className="flex items-center justify-between text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200">
                <span>
                  {level.status === 'LOCKED' ? (
                    <>
                      Complete <span className="font-semibold">Stage {index}</span> to unlock this stage
                    </>
                  ) : level.status === 'PENDING' ? (
                    <>
                      <span className="font-semibold">{(level.requiredLeaders - trackerData.leaderCount).toLocaleString()}</span> more leaders needed
                    </>
                  ) : (
                    <>
                      ✅ You have qualified for this stage!
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info Section */}
      <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <span>💡</span> About Bonanza Stages
        </h4>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>✓ Leaders are counted from your active team members (members with purchases)</li>
          <li>✓ Each stage builds on the previous one - qualify Stage 1 to unlock Stage 2</li>
          <li>✓ Rewards are handled by admin after you qualify each stage</li>
          <li>✓ Your leader count updates in real-time as your team members make purchases</li>
          <li>✓ Help your team stay active to grow your leader count faster!</li>
        </ul>
      </div>

      {/* Last Updated */}
      <div className="mt-6 text-center text-xs text-gray-500">
        Data updates automatically. Refresh the page to see latest changes.
      </div>
    </div>
  );
};

export default RealTimeBonanzaProgressTracker;
